import axios from "axios";
import feedback  from "@/components/ui/general/feedback/feedback";
import useGameStore from '@store/useGameStore';
import useAuthStore from '@store/useAuthStore';
import normalizeGameData from '@utils/normalizeGameData';
import { CONFIG } from '@utils/constants';
import { validateUnits } from '@utils/unitValidator';

const api = axios.create({
  baseURL: CONFIG.API_URL,
});

// Configuración de Axios para manejar errores y añadir el token de autorización
// Si la URL base no está definida, lanzamos un error.
if (!api.defaults.baseURL) {
  throw new Error("API base URL is not defined. Please check CONFIG.API_URL in constants.js.");
}


api.interceptors.request.use(config => {

  const token = localStorage.getItem('token');
  if (token) {
    // Si hay un token, lo añadimos a la cabecera de Authorization.
     //api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
     config.headers.Authorization = `Bearer ${token}`;
  }else
  {
    console.warn("🚀 Configuración de solicitud Axios no hay Bearer Token:", {
      url: config.url,
      method: config.method,
      headers: config.headers,
    });
  }

  return config;
}, error => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  response => {
    const data = response.data;
    // ✅ --- ¡LA LÓGICA DE SINCRONIZACIÓN CENTRALIZADA! --- ✅
    // Si la respuesta de la API contiene el objeto xhelos, actualizamos el store.
    if (data && data.data && data.data.xhelos) {
      // Normalize the game data ONCE here
      const normalizedGame = normalizeGameData(data.data.xhelos);
      
      // Validate units based on game status (only in debug mode)
      if (CONFIG.DEBUG_MODE) {
        const gameStatus = normalizedGame.status;
        
        // For game_plan and game_deploy phases - only validate current snapshot
        if (gameStatus === 'game_plan' || gameStatus === 'game_deploy') {
          if (normalizedGame.snapshots?.current?.dynamic_objects?.units) {
            validateUnits(normalizedGame.snapshots.current.dynamic_objects.units, 'API current snapshot (plan/deploy)');
          } else {
            console.error('[API Validator] Missing units in current snapshot for', gameStatus, 'phase');
          }
        }
        
        // For game_resolved phase - validate both base and current
        if (gameStatus === 'game_resolved') {
          // Validate base snapshot
          if (normalizedGame.snapshots?.base?.dynamic_objects?.units) {
            validateUnits(normalizedGame.snapshots.base.dynamic_objects.units, 'API base snapshot (resolved)');
          } else {
            console.error('[API Validator] Missing units in base snapshot for game_resolved phase');
          }
          
          // Validate current snapshot
          if (normalizedGame.snapshots?.current?.dynamic_objects?.units) {
            validateUnits(normalizedGame.snapshots.current.dynamic_objects.units, 'API current snapshot (resolved)');
          } else {
            console.error('[API Validator] Missing units in current snapshot for game_resolved phase');
          }
        }
      }
      
      // Call setGame directly from the store
      useGameStore.getState().setGame(normalizedGame);
    }
    else{
      //console.warn("[API]No game data found in response:", response);
    }


    const type = (data.status || 'success').toLowerCase();
    const userMsg = data.userMessage;
    const techMsg = data.message || '⚠️ No hay mensaje tecnico';

    // Mensaje técnico siempre
    if (techMsg) {
      //console.log(`📨 ${type.toUpperCase()} (${url}): ${techMsg}`);
    }

    // Feedback visible solo si está permitido
    if (data.userVisible !== false) {
      if (userMsg) {
        if (feedback[type]) {
          // Usamos un ícono más neutral para el éxito, ya que no siempre es una creación.
          feedback[type](`${userMsg}`); 
        } else {
          feedback.warning(`⚠️ Tipo de feedback desconocido: "${type}"`);
        }
      } else {
        // No mostramos advertencia si no hay mensaje de usuario, es un comportamiento esperado.
      }
    }

    // --- ¡LA CORRECCIÓN CLAVE ESTÁ AQUÍ! ---
    // En lugar de devolver el objeto 'response' completo, devolvemos solo 'response.data'.
    // 'response.data' es el cuerpo JSON que envía nuestro backend (el GameResponseDTO).
    return response.data;
  },

  error => {
    const res = error.response;

    if (!res) {
      feedback.error("❌ Network error or no response from server.");
      console.error("❌ Network error or no response from server.");
      return Promise.reject(error);
    }

    const data = res.data || {};
    const status = res.status;
    const url = res.config?.url || '';
    const userMsg = data.userMessage || null;
    const techMsg = data.message || 'Unknown error';
    const type = (data.status || 'error').toLowerCase();

     // 👇 Casos específicos (tu lógica original, mantenida)
    switch (status) {
      case 401:
        if (url.includes("/api/user")) {
          // Don't show message for initial user check - this is expected
          console.warn(`🔒 ${status} - Requiere autenticación para: ${url}`);
        } else {
          // For other 401 errors, show a message
          feedback.warning(`🔒 Sesión expirada. Por favor, inicia sesión nuevamente.`);
        }
        break;

      case 404:
        feedback.error(`❌ ${userMsg || 'Recurso no encontrado'}: ${url}`);
        console.warn(`⚠️ ${status} - No encontrado: ${techMsg}`);
        break;

      default:
        console.error(`💥 ${status} - ${techMsg}`);
        console.dir(data,url);
        if (status >= 500) {
          feedback.error(`❌ ${userMsg || 'Error del servidor'}. Intenta más tarde.`);
        } else if (status >= 400) {
          if (userMsg) {
            if (feedback[type]) {
              feedback[type](`❌ ${userMsg}`);
            } else {
              feedback.warning(`⚠️ Tipo de feedback desconocido: "${type}"`);
            }
          } else {
            console.warn("⚠️ No user message provided in response data.", url);
          }
        }
        break;
    }

    // Rechazamos la promesa con el cuerpo del error, para poder usarlo en los 'catch'.
    return Promise.reject(data);
  }
);

export default api;